import React, { useState, useEffect } from "react";
import api from "../utils/axiosInstance";
import { useModal } from "../context/ModalContext";
import {
  FaUpload,
  FaLink,
  FaCopy,
  FaCheckCircle,
  FaCloud,
  FaTrash,
  FaExternalLinkAlt,
  FaImage,
} from "react-icons/fa";
import {
  Card, CardHeader, CardBody,
  PageHeader, Button, Spinner,
  TableWrapper, Table, Th, Td,
  EmptyState,
} from "../components/ui";

const ALLOWED_EXT = /\.(jpg|jpeg|png|gif|webp|pdf|zip|docx?)$/i;
const MAX_SIZE = 100 * 1024 * 1024;

const ImageToURL = () => {
  const modal = useModal();
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploadedUrl, setUploadedUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [uploadsLoading, setUploadsLoading] = useState(true);
  const [hasCloudStorage, setHasCloudStorage] = useState(null);

  useEffect(() => {
    fetchUploads();
    checkStorageStatus();
  }, []);

  const fetchUploads = async () => {
    try {
      const res = await api.get("/api/upload");
      setUploads(res.data?.files || []);
    } catch {
      setUploads([]);
    } finally {
      setUploadsLoading(false);
    }
  };

  const checkStorageStatus = async () => {
    try {
      const res = await api.get("/api/settings/storage");
      setHasCloudStorage(!!res.data?.data);
    } catch {
      setHasCloudStorage(false);
    }
  };

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    setFile(selected);
    setUploadedUrl("");
    setCopied(false);
    if (selected.type.startsWith("image")) {
      setPreviewUrl(URL.createObjectURL(selected));
    } else {
      setPreviewUrl("");
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return modal.alert("Please select a file.", "warning");

    if (!ALLOWED_EXT.test(file.name)) {
      return modal.alert("Invalid file type. Only images, PDF, ZIP, DOCX allowed.", "warning");
    }

    if (file.size > MAX_SIZE) {
      return modal.alert("File size exceeds 100 MB limit.", "warning");
    }

    setLoading(true);
    setUploadedUrl("");

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await api.post("/api/upload", formData);
      setUploadedUrl(res.data.url);
      fetchUploads();
    } catch {
      modal.alert("Upload failed. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(uploadedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      modal.alert("Failed to copy to clipboard", "error");
    }
  };

  const clearAllUploads = async () => {
    const ok = await modal.confirm(
      "Delete all uploaded files? This cannot be undone.",
      { danger: true, confirmText: "Delete All" }
    );
    if (!ok) return;
    try {
      await api.delete("/api/upload");
      setUploads([]);
      modal.alert("All files deleted successfully.", "success");
    } catch {
      modal.alert("Failed to delete files.", "error");
    }
  };

  const deleteSingle = async (id, filename) => {
    const ok = await modal.confirm(`Delete "${filename}"? This cannot be undone.`, {
      danger: true,
      confirmText: "Delete",
    });
    if (!ok) return;
    try {
      await api.delete(`/api/upload/${id}`);
      setUploads((prev) => prev.filter((u) => u.id !== id));
    } catch {
      modal.alert("Failed to delete file.", "error");
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="page-content">
      <PageHeader
        title="File to URL"
        subtitle="Upload files and get shareable direct links"
        guideUrl="/guide/image-to-url"
      />

      {/* Storage indicator */}
      {hasCloudStorage !== null && (
        <div className={`flex items-center gap-2 text-xs mb-5 px-3 py-2 rounded-lg border ${
          hasCloudStorage
            ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800"
            : "bg-surface-bg text-ink-muted border-surface-border"
        }`}>
          <FaCloud size={12} />
          {hasCloudStorage
            ? "Uploading to your connected cloud storage (R2/S3)"
            : "Uploading to Walytic platform storage"
          }
          {!hasCloudStorage && (
            <a href="/settings#storage" className="text-brand hover:underline ml-1">
              Connect your own bucket
            </a>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        {/* Upload Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <FaUpload size={13} className="text-brand" />
              <span className="font-semibold text-ink-primary text-sm">Upload File</span>
            </div>
          </CardHeader>
          <CardBody>
            <form onSubmit={handleUpload}>
              <label className="flex flex-col items-center justify-center w-full px-4 py-6 rounded-lg border-2 border-dashed border-surface-border hover:border-brand/40 bg-surface-bg cursor-pointer transition-colors mb-4">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="max-h-32 rounded-lg mb-2 object-contain"
                  />
                ) : (
                  <FaImage size={28} className="text-ink-faint mb-2" />
                )}
                <span className="text-sm font-medium text-ink-secondary">
                  {file ? file.name : "Click to select a file"}
                </span>
                <span className="text-xs text-ink-faint mt-1">
                  JPG, PNG, GIF, WebP, PDF, ZIP, DOCX — max 100 MB
                </span>
                <input
                  type="file"
                  className="hidden"
                  accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.zip,.doc,.docx"
                  onChange={handleFileSelect}
                />
              </label>

              <Button
                variant="primary"
                className="w-full"
                type="submit"
                loading={loading}
                disabled={!file}
              >
                Upload & Get Link
              </Button>
            </form>
          </CardBody>
        </Card>

        {/* Result Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <FaLink size={13} className="text-brand" />
              <span className="font-semibold text-ink-primary text-sm">Generated URL</span>
            </div>
          </CardHeader>
          <CardBody className="flex flex-col justify-center">
            {uploadedUrl ? (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <FaCheckCircle className="text-green-500 flex-shrink-0" size={16} />
                  <span className="text-sm font-medium text-green-700 dark:text-green-400">
                    File uploaded successfully
                  </span>
                </div>

                <div className="flex items-center gap-2 p-3 rounded-lg bg-surface-bg border border-surface-border">
                  <input
                    readOnly
                    value={uploadedUrl}
                    className="input text-xs flex-1 !bg-transparent !border-0 !p-0 !ring-0"
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={copyToClipboard}
                    className="flex-shrink-0"
                  >
                    {copied ? <FaCheckCircle size={12} className="text-green-500" /> : <FaCopy size={12} />}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </div>

                <div className="flex gap-2 mt-3">
                  <a
                    href={uploadedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary btn-sm flex items-center gap-1.5"
                  >
                    <FaExternalLinkAlt size={10} />
                    Open
                  </a>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <FaLink size={24} className="text-ink-faint mb-3" />
                <p className="text-sm text-ink-muted">
                  Upload a file to generate a shareable URL
                </p>
                <p className="text-xs text-ink-faint mt-1">
                  {hasCloudStorage
                    ? "Files are uploaded to your R2/S3 bucket"
                    : "Files are stored on Walytic servers"
                  }
                </p>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Recent Uploads */}
      <Card>
        <CardHeader className="flex items-center justify-between">
          <span className="font-semibold text-ink-primary text-sm">
            Recent Uploads {uploads.length > 0 && `(${uploads.length})`}
          </span>
          {uploads.length > 0 && (
            <Button variant="danger" size="sm" onClick={clearAllUploads}>
              <FaTrash size={10} className="mr-1.5" />
              Clear All
            </Button>
          )}
        </CardHeader>
        {uploadsLoading ? (
          <CardBody>
            <div className="flex items-center justify-center py-8">
              <Spinner size="md" />
            </div>
          </CardBody>
        ) : uploads.length > 0 ? (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>#</Th>
                  <Th>Filename</Th>
                  <Th>Size</Th>
                  <Th>Storage</Th>
                  <Th>Uploaded</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {uploads.map((f, i) => (
                  <tr key={f.id || i}>
                    <Td className="text-ink-faint text-xs">{i + 1}</Td>
                    <Td>
                      <div className="font-medium text-sm truncate max-w-[200px]">{f.originalName || f.filename || f.name}</div>
                      <div className="text-xs text-ink-faint truncate max-w-[200px]">{f.url}</div>
                    </Td>
                    <Td className="text-xs text-ink-muted">{formatBytes(f.size)}</Td>
                    <Td>
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                        f.storageType === "cloud"
                          ? "bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
                          : "bg-surface-bg text-ink-muted"
                      }`}>
                        {f.storageType === "cloud" ? "R2/S3" : "Local"}
                      </span>
                    </Td>
                    <Td className="text-xs text-ink-muted">
                      {f.createdAt ? new Date(f.createdAt).toLocaleDateString() : "—"}
                    </Td>
                    <Td>
                      <div className="flex items-center gap-1">
                        <a
                          href={f.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-ghost btn-sm text-xs"
                        >
                          <FaExternalLinkAlt size={10} />
                        </a>
                        <button
                          onClick={() => deleteSingle(f.id, f.originalName || f.filename)}
                          className="btn-ghost btn-sm text-xs text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <FaTrash size={10} />
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        ) : (
          <CardBody>
            <EmptyState
              icon={FaUpload}
              title="No uploads yet"
              description="Uploaded files will appear here with their shareable URLs."
            />
          </CardBody>
        )}
      </Card>
    </div>
  );
};

export default ImageToURL;
